// CreateItemModifier.tsx - Redesigned to match AddItemToGroupModal from SelectionGroups

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  ListGroup,
  InputGroup,
  FormControl,
  Badge
} from 'react-bootstrap';
import { getAllItemsByLocalId } from '../../../../../../../../../../crud/menu_api/items';
import { AddModifierToItem } from '../../../../../../../../../../crud/menu_api/selection_modifiers';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import './CreateItemModifier.scss';
import { X } from 'react-bootstrap-icons';
import { FaSearch, FaCheck, FaPlus } from 'react-icons/fa';
import FilterDropdown from 'views/Restaurant/Menu/Menu/components/Items/components/Table/components/FilterDropdown/FilterDropdown';

interface Modifier {
  modifier_id: number;
  modifier_name: string;
  modifier_description?: string;
  local_id: number;
}

interface ItemModifier {
  item_id: number;
  item_name: string;
  modifier_id: number;
  is_optional: number;
  is_price_setter: number;
}

interface CreateItemModifierProps {
  show: boolean;
  handleClose: () => void;
  mod: Modifier;
  items: ItemModifier[];
  setItems: (items: ItemModifier[]) => void;
}

const CreateItemModifier: React.FC<CreateItemModifierProps> = ({
  show,
  handleClose,
  items,
  setItems,
  mod,
}) => {
  const [allItems, setAllItems] = useState<any[]>([]); // fetched raw
  const [availableItems, setAvailableItems] = useState<any[]>([]); // excludes already linked
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<any[]>([]); // Multiple selection support
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState<boolean>(true);

  // Categories for filtering
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);

  // Fetch raw items only when modal opens
  useEffect(() => {
    console.log('CreateItemModifier useEffect - show:', show);
    if (!show) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const localId = user ? user.local_id : 0;
    setLoadingItems(true);
    getAllItemsByLocalId(localId)
      .then(r => {
        setAllItems(r.data || []);
        // Extract unique categories from items
        const categories = new Map();
        (r.data || []).forEach((item: any) => {
          if (item.category_id && item.category_name) {
            categories.set(item.category_id, {
              category_id: item.category_id,
              category_name: item.category_name
            });
          }
        });
        setAllCategories(Array.from(categories.values()));
      })
      .catch(err => console.error('Error fetching items', err))
      .finally(() => setLoadingItems(false));
  }, [show]);

  // Derive available items (exclude existing linked items)
  useEffect(() => {
    const existingIds = new Set((items || []).map(i => i.item_id));
    setAvailableItems(allItems.filter((it: any) => !existingIds.has(it.item_id)));
  }, [allItems, items]);

  // Filter items based on search query and selected categories
  useEffect(() => {
    let base = availableItems || [];
    
    // Filter by categories first
    if (selectedCategories.length > 0) {
      const selectedCategoryIds = selectedCategories.map(cat => cat.value);
      base = base.filter((item: any) => selectedCategoryIds.includes(item.category_id));
    }
    
    // Then filter by search query
    const q = normalize(searchQuery);
    if (!q) { 
      setFilteredItems(base); 
      return; 
    }
    
    const tokens = q.split(' ').filter(Boolean);
    const filtered = base.filter((item: any) => {
      const haystack = normalize(item?.item_name + ' ' + item?.item_label);
      return tokens.every(tok => haystack.includes(tok));
    });
    setFilteredItems(filtered);
  }, [searchQuery, availableItems, selectedCategories]);

  // Normalization helper (remove accents, spaces, convert to lowercase)
  const normalize = (str: string) =>
    (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/\s+/g, ' ')
      .trim();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleItemSelect = (item: any) => {
    const isAlreadySelected = selectedItems.some(selected => selected.item_id === item.item_id);
    
    if (isAlreadySelected) {
      setSelectedItems(selectedItems.filter(selected => selected.item_id !== item.item_id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleRemoveSelectedItem = (itemId: number) => {
    setSelectedItems(selectedItems.filter(item => item.item_id !== itemId));
  };

  const isItemSelected = (itemId: number) => {
    return selectedItems.some(item => item.item_id === itemId);
  };

  const handleConfirmAdd = () => {
    if (selectedItems.length === 0) {
      toast.error('No se ha seleccionado ningún producto');
      return;
    }

    setConfirmLoading(true);

    // Create promises for all selected items with default values
    const addPromises = selectedItems.map(item => {
      const payload = {
        item_id: item.item_id,
        modifier_id: mod.modifier_id,
        is_price_setter: false, // Default
        is_optional: true, // Default
      };

      return AddModifierToItem(payload);
    });

    Promise.all(addPromises)
      .then((responses) => {
        toast.success(`${selectedItems.length} producto${selectedItems.length > 1 ? 's' : ''} vinculado${selectedItems.length > 1 ? 's' : ''} exitosamente`);
        
        // Add all new items to the items state
        const newItems = responses.map(response => (response as any).data);
        setItems([...items, ...newItems]);
        
        handleModalClose();
      })
      .catch((error) => {
        console.error('Error vinculando productos al modificador', error);
        const message = error.response?.data?.message || 'Error al vincular los productos';
        toast.error(message);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleAddAllFromCategory = () => {
    if (selectedCategories.length === 0) return;
    
    const selectedCategoryIds = selectedCategories.map(cat => cat.value);
    const categoryItems = availableItems.filter((item: any) => 
      selectedCategoryIds.includes(item.category_id)
    );
    
    if (categoryItems.length === 0) {
      toast.info('No hay productos disponibles en las categorías seleccionadas');
      return;
    }

    setConfirmLoading(true);

    // Create promises for all category items
    const addPromises = categoryItems.map(item => {
      const payload = {
        item_id: item.item_id,
        modifier_id: mod.modifier_id,
        is_price_setter: false,
        is_optional: true,
      };

      return AddModifierToItem(payload);
    });

    Promise.all(addPromises)
      .then((responses) => {
        const categoryNames = selectedCategories.map(cat => cat.label).join(', ');
        const message = selectedCategories.length === 1 
          ? `${categoryItems.length} productos de la categoría "${categoryNames}" vinculados al modificador`
          : `${categoryItems.length} productos de las categorías "${categoryNames}" vinculados al modificador`;
        toast.success(message);
        
        // Add all new items to the items state
        const newItems = responses.map(response => (response as any).data);
        setItems([...items, ...newItems]);
        
        handleModalClose();
      })
      .catch((error) => {
        console.error('Error vinculando productos de categorías al modificador', error);
        const message = error.response?.data?.message || 'Error al vincular los productos';
        toast.error(message);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleModalClose = () => {
    setSelectedItems([]);
    setSearchQuery('');
    setSelectedCategories([]);
    handleClose();
  };

  // Highlight matched tokens in text
  const highlightMatches = (text: string) => {
    if (!searchQuery.trim()) return text;
    const tokens = normalize(searchQuery).split(' ').filter(Boolean);
    if (!tokens?.length) return text;
    const norm = (s: string) => normalize(s);
    let remaining = text;
    const out: React.ReactNode[] = [];
    while (remaining?.length) {
      let earliest = -1;
      let token = '';
      for (const t of tokens) {
        const idx = norm(remaining).indexOf(t);
        if (idx !== -1 && (earliest === -1 || idx < earliest)) {
          earliest = idx;
          token = t;
        }
      }
      if (earliest === -1) {
        out.push(remaining);
        break;
      }
      if (earliest > 0) out.push(remaining.slice(0, earliest));
      const matchPortion = remaining.slice(earliest, earliest + token?.length);
      out.push(<mark key={out.length}>{matchPortion}</mark>);
      remaining = remaining.slice(earliest + token?.length);
    }
    return <>{out}</>;
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" className="cim-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Vincular productos a {mod.modifier_name}</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleModalClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body className="cim-body">
        {/* Multi-Selection Interface */}
        <div className="cim-item-selector">
          {/* Search Input */}
          <div className="cim-search-container">
            <div className="cim-search-row">
              <div className="cim-search-wrapper">
                <InputGroup className="cim-search-input">
                  <InputGroup.Text className="cim-search-icon">
                    <FaSearch size={10} />
                  </InputGroup.Text>
                  <FormControl
                    type="text"
                    placeholder="Busca por nombre del producto..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                    className="cim-search-field"
                    autoComplete="off"
                  />
                </InputGroup>
              </div>
              
              {/* Category Filter Dropdown */}
              <div className="cim-category-filter-wrapper">
                <FilterDropdown
                  options={allCategories.map(cat => ({
                    value: cat.category_id,
                    label: cat.category_name
                  }))}
                  selectedItems={selectedCategories}
                  onChange={setSelectedCategories}
                  placeholder="Filtrar por categorías"
                />
              </div>
            </div>
            
            {/* Add All from Category Button */}
            {selectedCategories.length > 0 && (
              <div className="cim-add-all-button-row">
                <Button
                  variant="none"
                  size="sm"
                  onClick={handleAddAllFromCategory}
                  className="cim-add-all-category-btn"
                  type="button"
                  disabled={confirmLoading}
                >
                  <FaPlus className="me-1" />
                  Vincular todos los productos de {selectedCategories.length === 1 ? 'la categoría' : 'las categorías'}
                </Button>
              </div>
            )}
          </div>

          {/* Selected Items Badges */}
          {selectedItems.length > 0 && (
            <div className="cim-selected-items-section">
              <div className="cim-selected-items-badges">
                {selectedItems.map((item) => (
                  <Badge 
                    key={item.item_id} 
                    className="cim-selected-item-badge"
                    bg="secondary"
                  >
                    <span className="cim-badge-text">{item.item_name}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="cim-badge-remove-btn"
                      onClick={() => handleRemoveSelectedItem(item.item_id)}
                      type="button"
                    >
                      <X size={12} />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Search Results */}
          <div className="cim-search-results">
            {loadingItems ? (
              <div className="cim-loading-state">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cargando productos...
              </div>
            ) : filteredItems.length > 0 ? (
              <>
                {searchQuery && (
                  <div className="cim-results-count">
                    {filteredItems.length} resultado{filteredItems.length !== 1 ? 's' : ''}
                  </div>
                )}
                
                <ListGroup className="cim-items-list">
                  {filteredItems.map((item) => (
                    <ListGroup.Item
                      key={item.item_id}
                      action
                      onClick={() => handleItemSelect(item)}
                      className={`cim-item-option ${isItemSelected(item.item_id) ? 'cim-selected' : ''}`}
                    >
                      <div className="cim-item-content">
                        <div className="cim-item-main-info">
                          <div className="cim-item-name-container">
                            <span className="cim-item-name">{highlightMatches(item.item_name)}</span>
                            {item.item_label && item.item_label !== item.item_name && (
                              <span className="cim-item-label"> • {highlightMatches(item.item_label)}</span>
                            )}
                          </div>
                        </div>
                        <div className="cim-item-price">
                          {item.price > 0 ? formatCurrency(item.price) : (
                            <span className="cim-free-price">Gratis</span>
                          )}
                        </div>
                        {isItemSelected(item.item_id) && (
                          <div className="cim-selected-indicator">
                            <FaCheck />
                          </div>
                        )}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </>
            ) : searchQuery ? (
              <div className="cim-no-results">
                Sin resultados para "{searchQuery}"
              </div>
            ) : (
              <div className="cim-empty-search">
                Escribe para buscar productos...
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="cim-selector-actions">
            <Button
              variant="secondary"
              onClick={handleModalClose}
              className="cim-cancel-btn"
              type="button"
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmAdd}
              className="cim-confirm-btn"
              type="button"
              disabled={selectedItems.length === 0 || confirmLoading}
            >
              {confirmLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Vinculando...
                </>
              ) : (
                <>
                  <FaCheck className="me-1" />
                  Vincular {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Empty State */}
        {availableItems.length === 0 && !loadingItems && (
          <div className="cim-empty-items-state">
            <span className="cim-empty-text">No hay productos disponibles para vincular.</span>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CreateItemModifier;
